import React, { useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { renderModal } from 'utils/modal_util';

import SubcatalogButtonContainer from 'components/features/ProductSidebar/SubcatalogButtonContainer';
import TooltipModalContent from 'components/shared/TooltipModalContent';
import Modal from 'components/shared/Modal';

import activeEditor from 'domains/active_editor';
import canvases from 'domains/canvases';

import type { TooltipModalContentProps } from 'components/shared/TooltipModalContent/TooltipModalContent';

type SubcatalogsProps = {
  setFlyoutOpen: (set: boolean) => void;
  setLeaveFlyoutOpen: (set: boolean) => void;
};

function Subcatalogs({ setFlyoutOpen, setLeaveFlyoutOpen }: SubcatalogsProps) {
  const [tooltipDialog, setTooltipDialog] = useState<React.ReactNode>(null);

  const dispatch = useAppDispatch();
  const activeCatalog = useAppSelector(activeEditor.selectors.catalog);
  const subcatalogs = useAppSelector(activeEditor.selectors.subcatalogs);

  const tooltipModalData: TooltipModalContentProps[] = useMemo(() => {
    return subcatalogs.map((subcatalog) => {
      const {
        id,
        parent_id: parentId,
        read_only: readOnly,
        tooltip_title: tooltipTitle,
        tooltip_description: tooltipDescription,
        tooltip_img: tooltipImg,
        tooltip_url: tooltipUrl,
      } = subcatalog;

      return {
        id,
        parentId,
        readOnly,
        tooltipTitle,
        tooltipDescription,
        tooltipImg,
        tooltipUrl,
        handleTooltipSelect,
      };
    });
  }, [subcatalogs]);

  function handleModalClose() {
    setTooltipDialog(null);
  }

  function handleTooltipSelect(id: number) {
    dispatch(canvases.actions.changeActiveCanvasConfig.subcatalog(id));
    setFlyoutOpen(false);
    setLeaveFlyoutOpen(false);
    handleModalClose();
  }

  function handleOpenTooltipModal(id: number, clickedButtonId: string) {
    const pageStartIndex = tooltipModalData.findIndex((subcatalog) => subcatalog.id === id);

    setTooltipDialog(
      <Modal
        labelledBy="subcatalog-tooltip-modal"
        lastFocusedElementId={clickedButtonId}
        onClose={handleModalClose}
        title={activeCatalog?.name || ''}
        pageStartIndex={pageStartIndex}
      >
        {tooltipModalData.map((subcatalog) => (
          <TooltipModalContent key={subcatalog.id} {...subcatalog} />
        ))}
      </Modal>
    );
  }

  return (
    <React.Fragment>
      <ul className="subcatalogs">
        {subcatalogs.map((subcatalog, index) => {
          return (
            <li key={subcatalog.id}>
              <SubcatalogButtonContainer
                subcatalog={subcatalog}
                handleOpenTooltipModal={handleOpenTooltipModal}
                setLeaveFlyoutOpen={setLeaveFlyoutOpen}
                setFlyoutOpen={setFlyoutOpen}
              />
            </li>
          );
        })}
      </ul>

      {tooltipDialog && renderModal(tooltipDialog)}
    </React.Fragment>
  );
}

export default Subcatalogs;
